import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getBaseUrl, ensureCsrfToken } from '../../shared/api';
import { authService } from '../../shared/authService';
import CopyButton from './components/CopyButton';
import DownloadButton from './components/DownloadButton';
import MessageText from './components/MessageText';
import ThinkingBlock from './components/ThinkingBlock';
import ToolCallCard from './components/ToolCallCard';
import useVoiceRecording from './useVoiceRecording';
import './AssistantChat.css';

const TTS_MAX_INPUT_CHARS = 900;

const plainTextForSpeech = (content) => {
  if (!content) return '';
  return content
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\(([^)]+)\)/g, ' ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/[`*_>#~\-|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const inferRouteContext = (pathname) => {
  const routePath = typeof pathname === 'string' ? pathname : '';
  const projectMatch = routePath.match(/^\/projects\/detail\/(\d+)(?:\/|$)/);
  if (projectMatch) {
    return {
      route_path: routePath,
      context_type: 'project',
      project_id: Number(projectMatch[1]),
    };
  }

  if (/^\/projects(?:\/|$)/.test(routePath)) {
    return { route_path: routePath, context_type: 'projects' };
  }
  if (/^\/warehouses(?:\/|$)/.test(routePath)) {
    return { route_path: routePath, context_type: 'warehouses' };
  }
  if (/^\/rentals(?:\/|$)/.test(routePath)) {
    return { route_path: routePath, context_type: 'rentals' };
  }

  return { route_path: routePath, context_type: 'general' };
};

const AssistantChat = ({ embedded = false, initialInput = '', externalTranscript = null, onWorkingChange }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeToolCalls, setActiveToolCalls] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(!embedded);
  useEffect(() => { setSidebarOpen(!embedded); }, [embedded]);
  const [imageMode, setImageMode] = useState(false);
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageEditMode, setImageEditMode] = useState(false);
  const [imageEditSize, setImageEditSize] = useState('1024x1024');
  const [toolMenuOpen, setToolMenuOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [thinkMode, setThinkMode] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [voicePlaybackBlocked, setVoicePlaybackBlocked] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const toolMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const { isRecording, isTranscribing, error: voiceError, startRecording, stopRecording } = useVoiceRecording({ language: i18n.language });

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pinnedRef = useRef(true);  // auto-scroll pinned to bottom
  const textareaRef = useRef(null);
  const streamControllerRef = useRef(null);
  const activeSessionIdRef = useRef(null);
  const inputRef = useRef('');
  const lastExternalTranscriptIdRef = useRef(null);
  const speakNextAssistantRef = useRef(false);
  const spokenAssistantIdsRef = useRef(new Set());
  const speechAudioRef = useRef(null);
  const speechRequestAbortRef = useRef(null);

  useEffect(() => {
    onWorkingChange?.(isStreaming || isSpeaking);
  }, [isSpeaking, isStreaming, onWorkingChange]);

  // ── Track user scroll to decide whether to pin ─────────────────────────
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      // Consider "at bottom" within 80px
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Close tool menu when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (toolMenuRef.current && !toolMenuRef.current.contains(e.target)) {
        setToolMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    activeSessionIdRef.current = activeSession?.id ?? null;
  }, [activeSession]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  useEffect(() => {
    return () => {
      if (speechRequestAbortRef.current) {
        speechRequestAbortRef.current.abort();
        speechRequestAbortRef.current = null;
      }
      if (speechAudioRef.current) {
        speechAudioRef.current.pause();
        speechAudioRef.current = null;
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Pre-fill input when opened via /assistant command from the chat room
  useEffect(() => {
    if (initialInput) {
      setInput(initialInput);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInput]);

  // ── Scroll to bottom only when pinned ─────────────────────────────────
  useEffect(() => {
    if (pinnedRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeToolCalls]);

  // ── Load sessions on mount ─────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    try {
      const resp = await fetch(`${getBaseUrl()}/assistant/api/sessions/`, {
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const sessionsData = data.results ?? data;
        setSessions(sessionsData);
        return sessionsData;
      }
      return [];
    } catch (e) {
      console.error('Failed to load sessions', e);
      return [];
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // ── Load a session's messages ──────────────────────────────────────────
  const openSession = useCallback(async (session) => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
    setIsStreaming(false);
    setActiveSession(session);
    setMessages([]);
    setActiveToolCalls([]);
    try {
      const resp = await fetch(`${getBaseUrl()}/assistant/api/sessions/${session.id}/`, {
        credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        const msgs = (data.messages ?? []).filter(
          (m) => m.role === 'user' || m.role === 'assistant'
        );
        setMessages(msgs.map((m) => ({ ...m, streaming: false })));
      }
    } catch (e) {
      console.error('Failed to load session messages', e);
    }
  }, []);

  // ── Start a new session ────────────────────────────────────────────────
  const newChat = () => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
    setIsStreaming(false);
    setActiveSession(null);
    activeSessionIdRef.current = null;
    setMessages([]);
    setActiveToolCalls([]);
    setInput('');
    setImageMode(false);
    setImageEditMode(false);
    setAttachedFiles([]);
  };

  // ── Delete a session ───────────────────────────────────────────────────
  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('assistant.confirmDelete'))) return;
    try {
      const csrf = await ensureCsrfToken();
      await fetch(`${getBaseUrl()}/assistant/api/sessions/${sessionId}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: csrf ? { 'X-CSRFToken': csrf } : {},
      });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSession?.id === sessionId) newChat();
    } catch (e) {
      console.error('Failed to delete session', e);
    }
  };

  // ── Rename a session ───────────────────────────────────────────────────
  const startRename = (s, e) => {
    e.stopPropagation();
    setRenamingId(s.id);
    setRenameValue(s.title);
  };

  const commitRename = async (sessionId) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (!title) return;
    try {
      const csrf = await ensureCsrfToken();
      await fetch(`${getBaseUrl()}/assistant/api/sessions/${sessionId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRFToken': csrf } : {}) },
        body: JSON.stringify({ title }),
        credentials: 'include',
      });
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title } : s)));
      if (activeSession?.id === sessionId) setActiveSession((s) => ({ ...s, title }));
    } catch (e) {
      console.error('Failed to rename session', e);
    }
  };

  // ── File / drag-and-drop ───────────────────────────────────────────────
  const handleFilesAdded = useCallback((fileList) => {
    const images = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    images.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedFiles((prev) => {
          if (prev.length >= 5) return prev; // cap at 5
          return [...prev, { id: `${Date.now()}-${Math.random()}`, file, dataUrl: reader.result }];
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFilesAdded(e.dataTransfer.files);
  }, [handleFilesAdded]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = Array.from(items)
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length > 0) handleFilesAdded(imageFiles);
  }, [handleFilesAdded]);

  // ── Image edit (img2img) ────────────────────────────────────────────────
  const editImage = async () => {
    const prompt = input.trim();
    const sourceImageFiles = attachedFiles.map((f) => f.file);
    if (!prompt || sourceImageFiles.length === 0 || isStreaming) return;

    if (streamControllerRef.current) streamControllerRef.current.abort();
    const controller = new AbortController();
    streamControllerRef.current = controller;

    setInput('');
    setAttachedFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setIsStreaming(true);
    setActiveToolCalls([]);

    const fileLabel = sourceImageFiles.length === 1
      ? sourceImageFiles[0].name
      : `${sourceImageFiles.length} images`;
    const userMsg = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: `🖼️ **${fileLabel}** — ${prompt}`,
      streaming: false,
    };
    setMessages((prev) => [...prev, userMsg]);

    const placeholderId = `streaming-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: placeholderId, role: 'assistant', content: '', streaming: true, isImage: true },
    ]);

    try {
      const fd = new FormData();
      sourceImageFiles.forEach((f) => fd.append('image', f));
      fd.append('prompt', prompt);
      fd.append('size', imageEditSize);
      if (activeSessionIdRef.current) fd.append('session_id', activeSessionIdRef.current);

      const resp = await fetch(`${getBaseUrl()}/assistant/api/edit-image/`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          switch (event.type) {
            case 'image':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === placeholderId
                    ? { ...m, content: `![ai-edited image](${event.url})\n\n*Prompt: ${event.prompt}*`, streaming: false }
                    : m
                )
              );
              break;
            case 'done':
              setMessages((prev) => {
                const updated = prev.map((m) =>
                  m.id === placeholderId ? { ...m, id: event.message_id ?? m.id, streaming: false } : m
                );
                if (event.message_id === null) return updated.filter((m) => m.id !== placeholderId);
                return updated;
              });
              if (event.session_id) {
                const sid = event.session_id;
                if (!activeSession) {
                  activeSessionIdRef.current = sid;
                  const refreshed = await loadSessions();
                  setActiveSession(refreshed.find((s) => s.id === sid) ?? { id: sid });
                } else {
                  await loadSessions();
                }
              }
              break;
            case 'error':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === placeholderId
                    ? { ...m, content: `⚠️ ${event.message}`, streaming: false, error: true }
                    : m
                )
              );
              break;
            default: break;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((m) =>
            m.streaming
              ? { ...m, content: `⚠️ ${t('assistant.errorSending')}: ${err.message}`, streaming: false, error: true }
              : m
          )
        );
      }
    } finally {
      setIsStreaming(false);
      setActiveToolCalls([]);
      streamControllerRef.current = null;
    }
  };

  // ── Generate image ─────────────────────────────────────────────────────
  const generateImage = async () => {
    const prompt = input.trim();
    if (!prompt || isStreaming) return;

    if (streamControllerRef.current) streamControllerRef.current.abort();
    const controller = new AbortController();
    streamControllerRef.current = controller;

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setIsStreaming(true);
    setActiveToolCalls([]);

    const userMsg = { id: `tmp-${Date.now()}`, role: 'user', content: prompt, streaming: false };
    setMessages((prev) => [...prev, userMsg]);

    const placeholderId = `streaming-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: placeholderId, role: 'assistant', content: '', streaming: true, isImage: true },
    ]);

    try {
      const resp = await fetch(`${getBaseUrl()}/assistant/api/generate-image/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          session_id: activeSessionIdRef.current,
          size: imageSize,
          source: embedded ? 'chat_embedded' : 'assistant',
        }),
        credentials: 'include',
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          switch (event.type) {
            case 'image':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === placeholderId
                    ? { ...m, content: `![ai-generated image](${event.url})\n\n*Prompt: ${event.prompt}*`, streaming: false, isImage: true }
                    : m
                )
              );
              break;
            case 'done':
              setMessages((prev) => {
                const updated = prev.map((m) =>
                  m.id === placeholderId ? { ...m, id: event.message_id ?? m.id, streaming: false } : m
                );
                if (event.message_id === null) return updated.filter((m) => m.id !== placeholderId);
                return updated;
              });
              if (event.session_id) {
                const newSessionId = event.session_id;
                if (!activeSession) {
                  activeSessionIdRef.current = newSessionId;
                  const refreshed = await loadSessions();
                  const found = refreshed.find((s) => s.id === newSessionId);
                  setActiveSession(found ?? { id: newSessionId });
                } else {
                  await loadSessions();
                }
              }
              break;
            case 'error':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === placeholderId
                    ? { ...m, content: `⚠️ ${event.message}`, streaming: false, error: true }
                    : m
                )
              );
              break;
            default:
              break;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((m) =>
            m.streaming
              ? { ...m, content: `⚠️ ${t('assistant.errorSending')}: ${err.message}`, streaming: false, error: true }
              : m
          )
        );
      }
    } finally {
      setIsStreaming(false);
      setActiveToolCalls([]);
      streamControllerRef.current = null;
    }
  };

  // ── Draft project from inquiry card ───────────────────────────────────
  const handleDraftFromInquiry = (parsed) => {
    const parts = ['Draft a project from this inquiry:'];
    if (parsed.event_name)   parts.push(`Name: ${parsed.event_name}`);
    if (parsed.date_from)    parts.push(`From: ${parsed.date_from}`);
    if (parsed.date_to)      parts.push(`To: ${parsed.date_to}`);
    if (parsed.location)     parts.push(`Location: ${parsed.location}`);
    if (parsed.customer_name) parts.push(`Customer: ${parsed.customer_name}`);
    if (parsed.notes)        parts.push(`Notes: ${parsed.notes}`);
    setInput(parts.join('\n'));
  };

  const speakAssistantReply = useCallback(async (content) => {
    const text = plainTextForSpeech(content).slice(0, TTS_MAX_INPUT_CHARS);
    if (!text) return;

    if (speechRequestAbortRef.current) {
      speechRequestAbortRef.current.abort();
      speechRequestAbortRef.current = null;
    }
    if (speechAudioRef.current) {
      speechAudioRef.current.pause();
      speechAudioRef.current = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    const abortController = new AbortController();
    speechRequestAbortRef.current = abortController;

    try {
      const resp = await fetch(`${getBaseUrl()}/assistant/api/tts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: abortController.signal,
        body: JSON.stringify({
          text,
          voice: 'cedar',
          response_format: 'wav',
          instructions: 'Speak clearly, naturally, and concisely in the same language as the input text.',
        }),
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const audioBlob = await resp.blob();
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('empty_audio');
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      speechAudioRef.current = audio;

      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        if (speechAudioRef.current === audio) speechAudioRef.current = null;
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        if (speechAudioRef.current === audio) speechAudioRef.current = null;
      };

      await audio.play();
      setIsSpeaking(true);
      setVoicePlaybackBlocked(false);
      speechRequestAbortRef.current = null;
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if ('speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined') {
        try {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = i18n.language || 'cs';
          window.speechSynthesis.speak(utterance);
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          setIsSpeaking(true);
          setVoicePlaybackBlocked(false);
          speechRequestAbortRef.current = null;
          return;
        } catch {
          // Continue to blocked state below.
        }
      }
      setVoicePlaybackBlocked(true);
      speechRequestAbortRef.current = null;
    }
  }, [i18n.language]);

  // ── Send message ───────────────────────────────────────────────────────
  const sendMessage = async (forcedText = null, options = {}) => {
    const fastMode = Boolean(options.fastMode);
    const text = (typeof forcedText === 'string' ? forcedText : input).trim();
    if (isStreaming || (!text && attachedFiles.length === 0)) return;

    // Abort any in-flight stream
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
    }
    const controller = new AbortController();
    streamControllerRef.current = controller;

    const filesToSend = [...attachedFiles];
    setInput('');
    setAttachedFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    pinnedRef.current = true;  // re-pin so the new message scrolls into view
    setIsStreaming(true);
    setActiveToolCalls([]);

    // Optimistically add user message (with file previews for display)
    const userMsg = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: text,
      files: filesToSend.map((f) => ({ name: f.file.name, dataUrl: f.dataUrl })),
      streaming: false,
    };
    setMessages((prev) => [...prev, userMsg]);

    // Add placeholder for assistant
    const assistantPlaceholderId = `streaming-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantPlaceholderId, role: 'assistant', content: '', streaming: true },
    ]);

    try {
      const reqHeaders = {};
      let reqBody;
      const source = embedded ? 'chat_embedded' : 'assistant';
      const routeContext = inferRouteContext(location.pathname);
      if (filesToSend.length > 0) {
        const fd = new FormData();
        fd.append('message', text || '[image]');
        if (activeSessionIdRef.current) fd.append('session_id', String(activeSessionIdRef.current));
        if (routeContext.route_path) fd.append('route_path', routeContext.route_path);
        if (routeContext.context_type) fd.append('context_type', routeContext.context_type);
        if (routeContext.project_id) fd.append('project_id', String(routeContext.project_id));
        if (fastMode) fd.append('fast_mode', '1');
        filesToSend.forEach((f) => fd.append('images', f.file));
        reqBody = fd;
      } else {
        reqHeaders['Content-Type'] = 'application/json';
        reqBody = JSON.stringify({
          message: text,
          session_id: activeSessionIdRef.current,
          think: fastMode ? false : thinkMode,
          fast_mode: fastMode,
          source,
          ...routeContext,
        });
      }
      let csrfToken = '';
      try {
        csrfToken = (await ensureCsrfToken()) || '';
      } catch {
        csrfToken = '';
      }
      if (csrfToken) {
        reqHeaders['X-CSRFToken'] = csrfToken;
      }
      try {
        const expires = authService.getTokenExpiry?.();
        if (expires && Date.now() / 1000 >= expires - 300) await authService.refreshToken();
      } catch (_) { /* continue — server will 401 if truly expired */ }
      const resp = await fetch(`${getBaseUrl()}/assistant/api/stream/`, {
        method: 'POST',
        headers: reqHeaders,
        body: reqBody,
        credentials: 'include',
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep partial last line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6);
          let event;
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          switch (event.type) {
            case 'token':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, content: m.content + event.content }
                    : m
                )
              );
              break;

            case 'thinking_token':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, thinking: (m.thinking || '') + event.content }
                    : m
                )
              );
              break;

            case 'tool_call':
              setActiveToolCalls((prev) => [
                ...prev,
                { id: Date.now(), name: event.name, args: event.args, result: null, ok: null },
              ]);
              break;

            case 'tool_result':
              setActiveToolCalls((prev) =>
                prev.map((tc) =>
                  tc.name === event.name && tc.result === null
                    ? { ...tc, result: event.result, ok: event.ok }
                    : tc
                )
              );
              break;

            case 'preview_card':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, previewCard: event.preview }
                    : m
                )
              );
              break;

            case 'readiness_card':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, readinessCard: event }
                    : m
                )
              );
              break;

            case 'inquiry_card':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, inquiryCard: event }
                    : m
                )
              );
              break;

            case 'done':
              setMessages((prev) => {
                const updated = prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, id: event.message_id ?? m.id, streaming: false }
                    : m
                );
                // Remove the placeholder entirely if LLM returned no text
                if (event.message_id === null) {
                  return updated.filter((m) => m.id !== assistantPlaceholderId);
                }
                return updated;
              });
              // Update active session and refresh session list
              if (event.session_id) {
                const newSessionId = event.session_id;
                if (!activeSession) {
                  activeSessionIdRef.current = newSessionId;
                  const refreshed = await loadSessions();
                  const found = refreshed.find((s) => s.id === newSessionId);
                  if (found) {
                    setActiveSession(found);
                  } else {
                    setActiveSession({ id: newSessionId });
                  }
                } else {
                  await loadSessions();
                }
              }
              break;

            case 'image':
              setMessages((prev) => {
                const idx = prev.findIndex((m) => m.id === assistantPlaceholderId);
                const imageMsg = {
                  id: event.message_id,
                  role: 'assistant',
                  content: `![ai-generated image](${event.url})\n\n*${event.prompt}*`,
                  streaming: false,
                  isImage: true,
                };
                if (idx === -1) return [...prev, imageMsg];
                return [...prev.slice(0, idx), imageMsg, ...prev.slice(idx)];
              });
              break;

            case 'error':
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantPlaceholderId
                    ? { ...m, content: `⚠️ ${event.message}`, streaming: false, error: true }
                    : m
                )
              );
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((m) =>
            m.streaming
              ? { ...m, content: `⚠️ ${t('assistant.errorSending')}: ${err.message}`, streaming: false, error: true }
              : m
          )
        );
      }
    } finally {
      setIsStreaming(false);
      setActiveToolCalls([]);
      streamControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (!externalTranscript?.id || !externalTranscript?.text) return;
    if (lastExternalTranscriptIdRef.current === externalTranscript.id) return;
    lastExternalTranscriptIdRef.current = externalTranscript.id;
    if (isStreaming || imageMode || imageEditMode) return;

    const combinedText = [inputRef.current, String(externalTranscript.text).trim()].filter(Boolean).join(' ').trim();
    if (!combinedText) return;

    setInput(combinedText);
    setTimeout(() => {
      speakNextAssistantRef.current = true;
      sendMessage(combinedText, { fastMode: true });
    }, 0);
  }, [externalTranscript, imageEditMode, imageMode, isStreaming]);

  useEffect(() => {
    if (!speakNextAssistantRef.current || messages.length === 0) return;

    const assistantMsg = [...messages].reverse().find(
      (m) => m.role === 'assistant' && !m.streaming && !m.error && !m.isImage && (m.content || '').trim()
    );
    if (!assistantMsg) return;

    const msgId = String(assistantMsg.id);
    if (spokenAssistantIdsRef.current.has(msgId)) return;
    spokenAssistantIdsRef.current.add(msgId);
    speakNextAssistantRef.current = false;
    speakAssistantReply(assistantMsg.content);
  }, [messages, speakAssistantReply]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (imageEditMode) editImage();
      else if (imageMode) generateImage();
      else sendMessage();
    }
  };

  const stopGeneration = () => {
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
    }
    setIsStreaming(false);
    setActiveToolCalls([]);
    // Mark any still-streaming message as done
    setMessages((prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m))
    );
  };

  // ── Format date ────────────────────────────────────────────────────────
  const formatDate = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className={`assistant-chat-container${embedded ? ' assistant-chat-container--embedded' : ''}`}>
      {/* Sidebar */}
      <aside className={`assistant-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="btn btn-primary btn-sm w-100" onClick={newChat}>
            <i className="ti ti-plus me-1" />
            {t('assistant.newChat')}
          </button>
          <button
            className="btn btn-ghost-secondary btn-icon sidebar-toggle ms-2"
            onClick={() => setSidebarOpen((v) => !v)}
            title={sidebarOpen ? t('assistant.hideSidebar') : t('assistant.showSidebar')}
          >
            <i className={`ti ${sidebarOpen ? 'ti-layout-sidebar-left-collapse' : 'ti-layout-sidebar-left-expand'}`} />
          </button>
        </div>

        {loadingSessions ? (
          <div className="p-3 text-muted text-center">
            <div className="spinner-border spinner-border-sm" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-3 text-muted small text-center">{t('assistant.noSessions')}</div>
        ) : (
          <ul className="session-list">
            {sessions.map((s) => (
              <li
                key={s.id}
                className={`session-item ${activeSession?.id === s.id ? 'active' : ''}`}
                onClick={() => renamingId !== s.id && openSession(s)}
              >
                {renamingId === s.id ? (
                  <input
                    className="session-rename-input"
                    value={renameValue}
                    autoFocus
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(s.id); }
                      if (e.key === 'Escape') { setRenamingId(null); }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div
                    className="session-title"
                    onDoubleClick={(e) => startRename(s, e)}
                    title={t('assistant.doubleClickToRename')}
                  >
                    {s.title}
                  </div>
                )}
                <div className="session-meta">
                  <span className="session-date">{formatDate(s.updated_at)}</span>
                  <button
                    className="btn btn-ghost-danger btn-icon btn-icon-sm session-delete"
                    onClick={(e) => deleteSession(s.id, e)}
                    title={t('assistant.deleteSession')}
                  >
                    <i className="ti ti-trash" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Main chat area */}
      <main
        className={`assistant-main${isDragOver && !imageMode && !imageEditMode ? ' drag-over' : ''}`}
        onDragOver={!imageMode && !imageEditMode ? handleDragOver : undefined}
        onDragLeave={!imageMode && !imageEditMode ? handleDragLeave : undefined}
        onDrop={!imageMode && !imageEditMode ? handleDrop : undefined}
      >
        {/* Drag overlay */}
        {isDragOver && !imageMode && !imageEditMode && (
          <div className="drag-overlay">
            <i className="ti ti-photo-up" style={{ fontSize: '2.5rem' }} />
            <span>{t('assistant.dropImagesHere')}</span>
          </div>
        )}
        {/* Header */}
        <div className="assistant-header">
          {!sidebarOpen && (
            <button
              className="btn btn-ghost-secondary btn-icon me-2"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ti ti-layout-sidebar-left-expand" />
            </button>
          )}
          <div className="assistant-header-title">
            <i className="ti ti-brain me-2 text-primary" />
            {activeSession ? activeSession.title : t('assistant.title')}
          </div>
        </div>

        {/* Messages */}
        <div className="assistant-messages" ref={messagesContainerRef}>
          {messages.length === 0 && !isStreaming && (
            <div className="assistant-empty">
              <i className="ti ti-brain ti-xl text-muted" />
              <p className="text-muted mt-3">{t('assistant.emptyState')}</p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`message-bubble ${msg.role} ${msg.error ? 'error' : ''}`}>
              <div className="message-avatar">
                {msg.role === 'user' ? (
                  <i className="ti ti-user" />
                ) : (
                  <i className="ti ti-brain" />
                )}
              </div>
              <div className="message-content-wrap">
                {msg.thinking && (
                  <ThinkingBlock thinking={msg.thinking} streaming={msg.streaming} />
                )}
                <div className="message-content">
                  {msg.files && msg.files.length > 0 && (
                    <div className="message-files">
                      {msg.files.map((f, i) => (
                        <img key={i} src={f.dataUrl} alt={f.name} className="message-file-img" />
                      ))}
                    </div>
                  )}
                  <MessageText content={msg.content} />
                  {msg.streaming && (
                    <span className="streaming-cursor" aria-hidden="true">▋</span>
                  )}
                </div>
                {msg.previewCard && (
                  <PreviewCard preview={msg.previewCard} t={t} />
                )}
                {msg.readinessCard && (
                  <ReadinessCard data={msg.readinessCard} t={t} />
                )}
                {msg.inquiryCard && (
                  <InquiryCard data={msg.inquiryCard} t={t} onDraft={handleDraftFromInquiry} />
                )}
                {!msg.streaming && msg.content && (
                  <div className="message-action-bar">
                    {msg.isImage && extractImageUrl(msg.content) && (
                      <DownloadButton url={extractImageUrl(msg.content)} />
                    )}
                    <CopyButton text={msg.content} />
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Active tool calls */}
          {activeToolCalls.length > 0 && (
            <div className="tool-calls-container">
              {activeToolCalls.map((tc) => (
                <ToolCallCard key={tc.id} toolCall={tc} />
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="assistant-input-area">
          {isStreaming && (
            <div className="thinking-indicator">
              {imageEditMode ? (
                <><i className="ti ti-wand me-2 text-warning" />{t('assistant.editingImage')}</>
              ) : imageMode ? (
                <><i className="ti ti-photo-ai me-2 text-primary" />{t('assistant.generatingImage')}</>
              ) : (
                <div className="spinner-border spinner-border-sm text-primary" aria-label={t('assistant.working', 'AI asistent pracuje')} />
              )}
            </div>
          )}

          {/* Active-mode chip */}
          {imageMode && (
            <div className="input-mode-chip">
              <i className="ti ti-photo-ai me-1" />
              {t('assistant.imageModeActive')}
              <select
                className="ms-2 form-select form-select-sm d-inline-block"
                style={{ width: 'auto' }}
                value={imageSize}
                onChange={(e) => setImageSize(e.target.value)}
                disabled={isStreaming}
              >
                <option value="1024x1024">1024×1024</option>
                <option value="1536x1024">1536×1024 (wide)</option>
                <option value="1024x1536">1024×1536 (tall)</option>
              </select>
              <button
                className="btn btn-close btn-sm ms-2"
                style={{ fontSize: '0.6rem' }}
                onClick={() => setImageMode(false)}
                title={t('assistant.exitImageMode')}
              />
            </div>
          )}

          {imageEditMode && (
            <div className="input-mode-chip input-mode-chip--edit">
              <i className="ti ti-wand me-1" />
              {t('assistant.imageEditModeActive')}
              <select
                className="ms-2 form-select form-select-sm d-inline-block"
                style={{ width: 'auto' }}
                value={imageEditSize}
                onChange={(e) => setImageEditSize(e.target.value)}
                disabled={isStreaming}
              >
                <option value="1024x1024">1024×1024</option>
                <option value="1536x1024">1536×1024 (wide)</option>
                <option value="1024x1536">1024×1536 (tall)</option>
              </select>
              <button
                className="btn btn-close btn-sm ms-2"
                style={{ fontSize: '0.6rem' }}
                onClick={() => { setImageEditMode(false); setAttachedFiles([]); }}
                title={t('assistant.exitImageMode')}
              />
            </div>
          )}

          {attachedFiles.length > 0 && !imageMode && (
            <div className="attached-files-strip">
              {attachedFiles.map((f) => (
                <div key={f.id} className="attached-file-thumb">
                  <img src={f.dataUrl} alt={f.file.name} />
                  <button
                    type="button"
                    className="attached-file-remove"
                    onClick={() => setAttachedFiles((prev) => prev.filter((x) => x.id !== f.id))}
                    disabled={isStreaming}
                    title={t('common.remove')}
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="input-row">
            {/* Tool picker dropdown */}
            <div className="position-relative" ref={toolMenuRef}>
              <button
                className={`btn btn-icon btn-ghost-secondary me-1 ${toolMenuOpen ? 'active' : ''}`}
                onClick={() => setToolMenuOpen((v) => !v)}
                title={t('assistant.tools')}
                disabled={isStreaming}
                type="button"
              >
                <i className="ti ti-sparkles" />
              </button>
              {toolMenuOpen && (
                <div className="tool-picker-menu">
                  <button
                    className={`tool-picker-item ${imageMode ? 'active' : ''}`}
                    onClick={() => { setImageMode(true); setImageEditMode(false); setToolMenuOpen(false); }}
                    type="button"
                  >
                    <i className="ti ti-photo-ai me-2 text-primary" />
                    {t('assistant.generateImage')}
                  </button>
                  <button
                    className={`tool-picker-item ${imageEditMode ? 'active' : ''}`}
                    onClick={() => { setImageEditMode(true); setImageMode(false); setToolMenuOpen(false); }}
                    type="button"
                  >
                    <i className="ti ti-wand me-2 text-warning" />
                    {t('assistant.editImage')}
                  </button>
                </div>
              )}
            </div>

            {/* Think mode toggle */}
            <button
              type="button"
              className={`btn btn-icon me-1 ${thinkMode ? 'btn-primary' : 'btn-ghost-secondary'}`}
              onClick={() => setThinkMode((v) => !v)}
              disabled={isStreaming || imageMode || imageEditMode}
              title={thinkMode ? t('assistant.thinkModeOn') : t('assistant.thinkModeOff')}
            >
              <i className="ti ti-brain" />
            </button>

            {!imageMode && (
              <>
                <button
                  type="button"
                  className="btn btn-icon btn-ghost-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isStreaming}
                  title={t('assistant.attachImage')}
                >
                  <i className="ti ti-paperclip" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { handleFilesAdded(e.target.files); e.target.value = ''; }}
                />
              </>
            )}

            {/* Push-to-talk voice input */}
            {!imageMode && !imageEditMode && (
              <button
                type="button"
                className={`btn btn-icon ms-1 ${
                  isRecording ? 'btn-danger' : 'btn-ghost-secondary'
                }`}
                onPointerDown={() => {
                  if (!isStreaming) startRecording((text) => {
                    if (text) {
                      const combinedText = [inputRef.current, String(text).trim()].filter(Boolean).join(' ').trim();
                      setInput(combinedText);
                      setTimeout(() => {
                        speakNextAssistantRef.current = true;
                        sendMessage(combinedText, { fastMode: true });
                        textareaRef.current?.focus();
                      }, 0);
                    }
                  });
                }}
                onPointerUp={stopRecording}
                onPointerCancel={stopRecording}
                onPointerLeave={() => { if (isRecording) stopRecording(); }}
                onTouchStart={(e) => e.preventDefault()}
                disabled={isStreaming}
                title={isRecording ? t('assistant.recording') : isTranscribing ? t('assistant.transcribing') : t('assistant.voiceInput')}
              >
                {isTranscribing
                  ? <span className="spinner-border spinner-border-sm" />
                  : <i className={`ti ${isRecording ? 'ti-microphone-off' : 'ti-microphone'}`} />}
              </button>
            )}
            {voiceError && (
              <span className="text-danger small ms-1" title={voiceError}>
                <i className="ti ti-alert-triangle" />
              </span>
            )}
            {voicePlaybackBlocked && (
              <button
                type="button"
                className="btn btn-sm btn-outline-warning ms-1"
                onClick={() => {
                  setVoicePlaybackBlocked(false);
                  const assistantMsg = [...messages].reverse().find(
                    (m) => m.role === 'assistant' && !m.streaming && !m.error && !m.isImage && (m.content || '').trim()
                  );
                  if (assistantMsg) speakAssistantReply(assistantMsg.content);
                }}
                title={t('assistant.enableVoicePlayback', 'Enable voice playback')}
              >
                <i className="ti ti-volume me-1" />
                {t('assistant.enableVoicePlayback', 'Enable voice playback')}
              </button>
            )}

            <textarea
              ref={textareaRef}
              className="form-control"
              placeholder={
                imageEditMode
                  ? t('assistant.imageEditPlaceholder')
                  : imageMode
                    ? t('assistant.imagePlaceholder')
                    : attachedFiles.length > 0
                      ? t('assistant.inputPlaceholderWithFiles')
                      : t('assistant.inputPlaceholder')
              }
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
              }}
              onKeyDown={handleKeyDown}
              onPaste={!imageMode && !imageEditMode ? handlePaste : undefined}
              rows={1}
              disabled={isStreaming}
            />
            {isStreaming ? (
              <button
                type="button"
                className="btn btn-danger ms-2"
                onClick={stopGeneration}
                title={t('assistant.stopGeneration')}
              >
                <i className="ti ti-player-stop-filled" />
              </button>
            ) : (
              <button
                className={`btn ms-2 ${imageEditMode ? 'btn-warning' : imageMode ? 'btn-secondary' : 'btn-primary'}`}
                onClick={imageEditMode ? editImage : imageMode ? generateImage : sendMessage}
                disabled={
                  (imageEditMode ? (!input.trim() || attachedFiles.length === 0) :
                   imageMode ? !input.trim() :
                   (!input.trim() && attachedFiles.length === 0))
                }
              >
              {imageEditMode
                ? <i className="ti ti-wand" />
                : imageMode
                  ? <i className="ti ti-photo-ai" />
                  : <i className="ti ti-send" />
              }
            </button>
            )}
          </div>
          <div className="input-hint">{t('assistant.inputHint')}</div>
          <div className="input-hint text-muted">{t('assistant.aiVoiceDisclosure', 'Voice responses are AI-generated.')}</div>
        </div>
      </main>
    </div>
  );
};

// ── Subcomponents ──────────────────────────────────────────────────────────

// Extract the first image URL from a markdown string: ![...](url)
const extractImageUrl = (content) => {
  if (!content) return null;
  const m = content.match(/!\[[^\]]*\]\(([^)]+)\)/);
  return m ? m[1] : null;
};

const PreviewCard = ({ preview, t }) => {
  const [checked, setChecked] = useState(() => {
    const s = new Set();
    preview.forEach((item) => { if (item.risk !== 'critical') s.add(item.id); });
    return s;
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allChecked = checked.size === preview.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(preview.map((i) => i.id)));

  const handleSave = async () => {
    const approved = preview.filter((i) => checked.has(i.id));
    if (!approved.length) return;
    setSaving(true);
    let saved = 0;
    const errors = [];
    await Promise.all(
      approved.map(async (item) => {
        try {
          const proposed = { ...item.proposed };
          if (proposed.item_type !== undefined && proposed.product_type === undefined) {
            proposed.product_type = proposed.item_type;
            delete proposed.item_type;
          }
          const resp = await fetch(`${getBaseUrl()}/warehouses/api/products/${item.id}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(proposed),
            credentials: 'include',
          });
          if (resp.ok) saved++; else errors.push(`#${item.id}`);
        } catch { errors.push(`#${item.id}`); }
      })
    );
    setSaving(false);
    setResult({ saved, errors });
  };

  const riskMeta = (risk) => {
    if (risk === 'critical') return { cls: 'preview-risk--critical', icon: 'ti-alert-triangle', label: t('assistant.riskCritical') };
    if (risk === 'warning')  return { cls: 'preview-risk--warning',  icon: 'ti-alert-circle',   label: t('assistant.riskWarning') };
    return                          { cls: 'preview-risk--safe',     icon: 'ti-circle-check',   label: t('assistant.riskSafe') };
  };

  const changesSummary = (proposed) =>
    Object.entries(proposed)
      .filter(([k]) => k !== 'id')
      .map(([k, v]) => `${k}: ${String(v).slice(0, 80)}`)
      .join(' · ');

  if (result) {
    return (
      <div className="preview-card preview-card--done">
        <i className="ti ti-circle-check text-success me-2" />
        {t('assistant.previewSaved', { count: result.saved })}
        {result.errors.length > 0 && (
          <span className="ms-2 text-danger small">
            {t('assistant.previewErrors', { items: result.errors.join(', ') })}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="preview-card">
      <div className="preview-card-header">
        <i className="ti ti-list-check me-2 text-primary" />
        <strong>{t('assistant.previewTitle', { count: preview.length })}</strong>
        <label className="ms-auto preview-card-select-all">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          <span>{t('assistant.selectAll')}</span>
        </label>
      </div>
      <div className="preview-card-body">
        <table className="preview-table">
          <thead>
            <tr>
              <th />
              <th>{t('common.id', 'ID')}</th>
              <th>{t('common.name', 'Name')}</th>
              <th>{t('assistant.previewChanges')}</th>
              <th>{t('assistant.previewRisk')}</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((item) => {
              const { cls, icon } = riskMeta(item.risk);
              return (
                <tr key={item.id} className={checked.has(item.id) ? 'preview-row--checked' : ''}>
                  <td><input type="checkbox" checked={checked.has(item.id)} onChange={() => toggle(item.id)} /></td>
                  <td className="preview-cell-id text-muted small">#{item.id}</td>
                  <td className="preview-cell-name">{item.current?.name || '—'}</td>
                  <td className="preview-cell-changes small">{changesSummary(item.proposed)}</td>
                  <td>
                    <span className={`preview-risk-badge ${cls}`} title={item.risk_reason}>
                      <i className={`ti ${icon}`} /> {label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="preview-card-footer">
        <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving || checked.size === 0}>
          {saving
            ? <><div className="spinner-border spinner-border-sm me-1" />{t('common.saving', 'Saving…')}</>
            : <><i className="ti ti-device-floppy me-1" />{t('assistant.saveApproved', { count: checked.size })}</>
          }
        </button>
        <button className="btn btn-sm btn-ghost-secondary ms-2" onClick={() => setChecked(new Set())} disabled={saving || checked.size === 0}>
          {t('assistant.deselectAll')}
        </button>
      </div>
    </div>
  );
};

// ── ReadinessCard ──────────────────────────────────────────────────────────
const ReadinessCard = ({ data, t }) => {
  const { score, checks, summary, project_name, project_id } = data;
  const scoreColor = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
  const statusIcon = (s) => {
    if (s === 'ok')      return 'ti-circle-check text-success';
    if (s === 'missing') return 'ti-circle-x text-danger';
    return 'ti-alert-circle text-warning';
  };
  return (
    <div className="readiness-card">
      <div className="readiness-card-header">
        <i className="ti ti-checklist me-2" />
        <strong>{t('assistant.readinessTitle', { name: project_name || `#${project_id}` })}</strong>
        <div className="readiness-score-pill ms-auto" style={{ background: scoreColor }}>
          {score}<span className="readiness-score-max">/100</span>
        </div>
      </div>
      <div className="readiness-card-body">
        {checks && checks.map((c) => (
          <div key={c.name} className="readiness-check-row">
            <i className={`ti ${statusIcon(c.status)} me-2`} />
            <span className="readiness-check-detail">{c.detail}</span>
          </div>
        ))}
      </div>
      {summary && <div className="readiness-card-summary text-muted small">{summary}</div>}
    </div>
  );
};

// ── InquiryCard ────────────────────────────────────────────────────────────
const InquiryCard = ({ data, t, onDraft }) => {
  const fields = [
    { key: 'event_name',      label: t('assistant.inquiryEventName',    'Event name'),    icon: 'ti-calendar-event' },
    { key: 'date_from',       label: t('assistant.inquiryDateFrom',     'Date from'),     icon: 'ti-calendar' },
    { key: 'date_to',         label: t('assistant.inquiryDateTo',       'Date to'),       icon: 'ti-calendar' },
    { key: 'location',        label: t('assistant.inquiryLocation',     'Location'),      icon: 'ti-map-pin' },
    { key: 'event_type',      label: t('assistant.inquiryEventType',    'Event type'),    icon: 'ti-confetti' },
    { key: 'customer_name',   label: t('assistant.inquiryCustomer',     'Customer'),      icon: 'ti-building' },
    { key: 'customer_contact',label: t('assistant.inquiryContact',      'Contact'),       icon: 'ti-at' },
    { key: 'crew_count',      label: t('assistant.inquiryCrew',         'Crew needed'),   icon: 'ti-users' },
    { key: 'budget',          label: t('assistant.inquiryBudget',       'Budget'),        icon: 'ti-currency-koruna' },
  ];
  const hasMissing = data.missing && data.missing.length > 0;
  const hasItems   = data.items_mentioned && data.items_mentioned.length > 0;
  return (
    <div className="inquiry-card">
      <div className="inquiry-card-header">
        <i className="ti ti-mail-opened me-2" />
        <strong>{t('assistant.inquiryCardTitle', 'Extracted Inquiry Data')}</strong>
      </div>
      <div className="inquiry-card-body">
        {fields.map(({ key, label, icon }) =>
          data[key] != null && data[key] !== '' ? (
            <div key={key} className="inquiry-field-row">
              <i className={`ti ${icon} inquiry-field-icon`} />
              <span className="inquiry-field-label">{label}:</span>
              <span className="inquiry-field-value">{String(data[key])}</span>
            </div>
          ) : null
        )}
        {hasItems && (
          <div className="inquiry-field-row">
            <i className="ti ti-packages inquiry-field-icon" />
            <span className="inquiry-field-label">{t('assistant.inquiryItems', 'Equipment')}:</span>
            <span className="inquiry-field-value">{data.items_mentioned.join(', ')}</span>
          </div>
        )}
        {hasMissing && (
          <div className="inquiry-missing">
            <i className="ti ti-alert-circle text-warning me-1" />
            {t('assistant.inquiryMissing', 'Missing info')}: {data.missing.join(', ')}
          </div>
        )}
      </div>
      <div className="inquiry-card-footer">
        <button className="btn btn-sm btn-primary" onClick={() => onDraft && onDraft(data)}>
          <i className="ti ti-plus me-1" />
          {t('assistant.inquiryCreateProject', 'Create project')}
        </button>
      </div>
    </div>
  );
};

export default AssistantChat;
